import type { PortfolioFilters, PortfolioFiltersFormType, PortfolioType } from "~/types/Portfolio";
import { endpoints } from "~/constants/endpoints";

export function usePortfolio(initialSpecificationId: string) {
    const route = useRoute();
    const globalStore = useGlobalStore();
    const { specifications } = globalStore;

    const isLoaded = ref(false);
    const mastersList = ref<PortfolioType[]>([]);
    const total = ref(0);
    const filtersList = ref<PortfolioFilters | null>(null);

    const filterForm = ref<PortfolioFiltersFormType>({
        specificationId: [initialSpecificationId],
        city: [],
        educationType: [],
        employmentType: [],
        isRemote: null,
        minLat: null,
        maxLat: null,
        minLng: null,
        maxLng: null,
        priceFrom: null,
        priceTo: null,
        pageSize: 15,
        page: 1,
        reviewsCount: null,
        experience: null
    });

    const reviewCounts = [
        {
            label: "No reviews",
            value: 0,
        },
        {
            label: "From 1 to 3 reviews",
            value: 3,
        },
        {
            label: "From 4 to 6 reviews",
            value: 6,
        },
        {
            label: "From 7 to 9 reviews",
            value: 9,
        },
        {
            label: "More than 10 reviews",
            value: 11,
        },
    ];

    const experienceCounts = [
        {
            label: "No experience",
            value: 0,
        },
        {
            label: "From 1 to 3 years",
            value: 3,
        },
        {
            label: "From 3 to 6 years",
            value: 6,
        },
        {
            label: "More than 6 years",
            value: 7,
        },
    ]

    const currentSpecification = computed(() => {
        return specifications.find(item => item.documentId === filterForm.value.specificationId[0]);
    });

    const parentSpecification = computed(() => {
        if (!currentSpecification.value) return null;
        if (!currentSpecification.value.parent_id) return currentSpecification.value;
        return specifications.find(item => item.documentId === currentSpecification.value?.parent_id);
    });

    const specificationChildList = computed(() => {
        if (!parentSpecification.value?.documentId) return [];
        return globalStore.childSpecifications(parentSpecification.value.documentId);
    });

    async function getFilters() {
        if (!filterForm.value.specificationId[0]) return null;
        isLoaded.value = false;

        const response = await fetchData<PortfolioFilters>(endpoints.portfolio.filters, {
            method: 'GET',
            query: {
                specificationId: filterForm.value.specificationId[0]
            }
        }).then(({data}) => {
            if(data?.prices && Array.isArray(data.prices)){
                data.price = {
                    from: Math.min(...data.prices.map(p => p.from)),
                    to: Math.max(...data.prices.map(p => p.to))
                }
            }
            data.reviews = reviewCounts
            data.experiences = experienceCounts
            return data;
        }).catch(e => {
            console.error(e);
        }).finally(() => {
            isLoaded.value = true;
        });

        filtersList.value = response || null;
        return filtersList.value;
    }

    const cleanedFilterForm = computed(() => {
        const form = { ...filterForm.value }

        return Object.entries(form).reduce((acc, [key, value]) => {
            // Пропускаем null, undefined и пустые массивы
            if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
                return acc
            }

            if (Array.isArray(value)) {
                acc[key] = value.join(',')
                return acc
            }

            acc[key] = value
            return acc
        }, {})
    })

    async function getMasters() {
        if (!currentSpecification.value?.documentId) return [];
        //console.log(cleanedFilterForm.value)
        isLoaded.value = false;
        const response = await fetchData<PortfolioType[]>(endpoints.portfolio.search, {
            method: 'GET',
            query: cleanedFilterForm.value
        }).then(({data, meta}) => {
            if (meta?.pagination?.total) total.value = meta.pagination.total;
            return data;
        }).catch(e => {
            console.error(e);
        }).finally(() => {
            isLoaded.value = true;
        });

        mastersList.value = response?.filter(portfolio => !!portfolio.user) || []
        if(process.client){
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    return {
        isLoaded,
        filterForm,
        mastersList,
        total,
        filtersList,
        currentSpecification,
        parentSpecification,
        specificationChildList,
        cleanedFilterForm,
        getFilters,
        getMasters,
    };
}